import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state", "value", "checked"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useCallback, useRef, useEffect } from 'react';
import { useId, useLiveRef, useTagName, useEvent, useForkRef } from 'ariakit-utils/hooks';
import { useStore, createMemoComponent } from 'ariakit-utils/store';
import { createHook, createElement } from 'ariakit-utils/system';
import { useCompositeItem } from '../composite/composite-item.js';
import { R as RadioContextState } from '../__utils-6a5ab864.js';

function getIsChecked(value, stateValue) {
  if (stateValue === undefined) return;

  if (value != null && stateValue != null) {
    return stateValue === value;
  }

  return !!stateValue;
}

function isNativeRadio(tagName, type) {
  return tagName === "input" && (!type || type === "radio");
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a radio button element.
 * @see https://ariakit.org/components/radio
 * @example
 * ```jsx
 * const state = useRadioState();
 * const props = useRadio({ state, value: "Apple" });
 * <RadioGroup state={state}>
 *   <Role as="input" {...props} />
 *   <Radio value="Orange" />
 * </RadioGroup>
 * ```
 */


var useRadio = createHook(function (_ref) {
  var _state, _state2, _state4;

  var state = _ref.state,
      value = _ref.value,
      checked = _ref.checked,
      props = _objectWithoutProperties(_ref, _excluded);

  var id = useId(props.id);
  state = useStore(state || RadioContextState, [useCallback(function (s) {
    return s.activeId === id;
  }, [id]), useCallback(function (s) {
    return s.value === value;
  }, [value]), "setActiveId", "setValue"]);
  var ref = useRef(null);
  var isActiveItemRef = useLiveRef(((_state = state) == null ? void 0 : _state.activeId) === id);
  var isChecked = checked != null ? checked : getIsChecked(value, (_state2 = state) == null ? void 0 : _state2.value); // When the radio state has a default value, we need to update the active id
  // to point to the checked element, otherwise it'll be the first item in the
  // list.

  useEffect(function () {
    if (!isActiveItemRef.current && isChecked) {
      var _state3;

      (_state3 = state) == null ? void 0 : _state3.setActiveId(id);
    }
  }, [isChecked, (_state4 = state) == null ? void 0 : _state4.setActiveId, id]);
  var onChangeProp = props.onChange;
  var tagName = useTagName(ref, props.as || "input");
  var nativeRadio = isNativeRadio(tagName, props.type);
  var onChange = useEvent(function (event) {
    var _state5;

    if (props.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!nativeRadio) {
      event.currentTarget.checked = true;
    }

    onChangeProp == null ? void 0 : onChangeProp(event);
    if (event.defaultPrevented) return;
    (_state5 = state) == null ? void 0 : _state5.setValue(value);
  });
  var onClickProp = props.onClick;
  var onClick = useEvent(function (event) {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    if (nativeRadio) return;
    onChange(event);
  });
  var onFocusProp = props.onFocus;
  var onFocus = useEvent(function (event) {
    var _state6;

    onFocusProp == null ? void 0 : onFocusProp(event);
    if (event.defaultPrevented) return;
    if (!nativeRadio) return;
    if (!((_state6 = state) != null && _state6.moves)) return;
    if (!isActiveItemRef.current) return;
    onChange(event);
  });
  props = _objectSpread(_objectSpread({
    id: id,
    role: !nativeRadio ? "radio" : undefined,
    type: nativeRadio ? "radio" : undefined,
    "aria-checked": isChecked
  }, props), {}, {
    ref: useForkRef(ref, props.ref),
    onChange: onChange,
    onClick: onClick,
    onFocus: onFocus
  });
  props = useCompositeItem(_objectSpread({
    state: state,
    clickOnEnter: !nativeRadio
  }, props));
  return _objectSpread({
    value: nativeRadio ? value : undefined,
    checked: isChecked
  }, props);
});
/**
 * A component that renders a radio button element.
 * @see https://ariakit.org/components/radio
 * @example
 * ```jsx
 * const radio = useRadioState();
 * <RadioGroup state={radio}>
 *   <Radio value="Apple" />
 *   <Radio value="Orange" />
 * </RadioGroup>
 * ```
 */

var Radio = createMemoComponent(function (props) {
  var htmlProps = useRadio(props);
  return createElement("input", htmlProps);
});
export { Radio, useRadio };